'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { carregarLogos } from '@/utils/carregar-logos';
import styles from '@/styles/highlight-jobs.module.css';
import { SuperHighlightJob } from '@/types/job';
import { updateSlickSlideFocusability } from '@/utils/slick-a11y';

import SuperHighlightJobCard from './super-highlight-job-card';

const SlickSlider = Slider as unknown as React.ComponentType<{
  children?: React.ReactNode;
  className?: string;
  [key: string]: unknown;
}>;

const settings = {
  centerMode: false,
  dots: false,
  infinite: false,
  speed: 500,
  slidesToShow: 2,
  slidesToScroll: 2,
  rows: 1,
  slidesPerRow: 1,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
        centerMode: false,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
        centerMode: false,
        initialSlide: 0,
      },
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: true,
        initialSlide: 0,
      },
    },
    {
      breakpoint: 320,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: false,
        initialSlide: 0,
      },
    },
  ],
};

interface SuperHighlightJobsProps {
  jobs: SuperHighlightJob[];
}

// 2 cards visiveis no desktop, mais 2 de antecipacao. Ver
// src/utils/carregar-logos.ts para o porque de nao buscar todos de uma vez.
const JANELA = 4;

export default function SuperHighlightJobs({ jobs }: SuperHighlightJobsProps) {
  const [jobsLogo, setJobsLogo] = useState<string[]>([]);
  const sliderRootRef = useRef<HTMLDivElement>(null);
  const logosPedidos = useRef(new Set<number>());
  const pedidosAte = useRef(0);

  const updateSlideFocusability = useCallback(() => {
    updateSlickSlideFocusability(sliderRootRef.current);
  }, []);

  const carregarProximos = useCallback(async () => {
    const ids = jobs.map((job) => job.id);
    pedidosAte.current = Math.min(pedidosAte.current + JANELA, ids.length);

    const parcial = await carregarLogos(ids, pedidosAte.current, logosPedidos.current);
    if (!parcial) return;

    setJobsLogo((anterior) => {
      const proximo = [...anterior];
      for (const [indice, logo] of Object.entries(parcial)) {
        proximo[Number(indice)] = logo;
      }
      return proximo;
    });
  }, [jobs]);

  useEffect(() => {
    // Lista nova zera a janela e os logos ja carregados: os indices sao
    // posicionais, entao manter os antigos casaria logo com a vaga errada.
    logosPedidos.current = new Set<number>();
    pedidosAte.current = 0;
    setJobsLogo([]);

    if (jobs?.length > 0) {
      carregarProximos();
    }
  }, [jobs, carregarProximos]);

  useEffect(() => {
    const animationFrame = window.requestAnimationFrame(updateSlideFocusability);

    return () => window.cancelAnimationFrame(animationFrame);
  }, [jobsLogo, updateSlideFocusability]);

  if (!jobs?.length) return null;

  return (
    <div className={`${styles.highlight} ${styles.super}`}>
      <div className="container">
        <div className="row">
          <div className="col-12 d-flex flex-column align-items-center">
            <h2>Super destaques</h2>

            <div ref={sliderRootRef} className={styles.highlight_list}>
              <SlickSlider
                {...settings}
                className={styles.highlight_list}
                onInit={updateSlideFocusability}
                onReInit={updateSlideFocusability}
                afterChange={updateSlideFocusability}
                beforeChange={carregarProximos}
              >
                {jobs.map((job, index) => (
                  <SuperHighlightJobCard
                    key={job.id}
                    data={{
                      ...job,
                      companyLogo: jobsLogo[index],
                    }}
                  />
                ))}
              </SlickSlider>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
